import React from 'react';

import { Button } from '@lightspeed/unified-components-default-consumer-theme/react';
import { T } from '@transifex/react';
import { useFormContext } from 'react-hook-form';

import { RecaptchaFailed } from '@hospitality-consumer-facing/components';
import { WIDGET_MESSAGE } from '@hospitality-consumer-facing/lsk-reservation/shared';

import { ReservationFormData } from '../../../reservation/types';
import { RecaptchaNoteWithText } from '../../../shared/components/recaptcha-note-with-text';
import { ReservationErrorMessage } from '../../../shared/components/reservation-error-message';
import { ReservationSummary } from '../../../shared/components/reservation-summary';
import { RECAPTCHA_ACTIONS } from '../../../shared/constants/constants';
import { useReservationSubmit } from '../../../shared/hooks/use-reservation-submit.hook';
import { BusinessLocation, ReservationConfirmation } from '../../../shared/types';
import { useWidgetCommunicationContext } from '../../providers/widget-communication.provider';

import { StickyFooter } from './sticky-footer';

interface ReservationActionStepProps {
  businessLocation: BusinessLocation;
  onSuccess: (result: ReservationConfirmation) => void;
}

export function ReservationActionStep({ businessLocation, onSuccess }: ReservationActionStepProps) {
  const { watch, getValues } = useFormContext<ReservationFormData>();
  const utcScheduledAt = watch('utcScheduledAt');
  const partySize = watch('partySize');
  const guestEmail = watch('guest.email');

  const { sendMessage } = useWidgetCommunicationContext();

  const { isLoading, isRecaptchaError, reservationError, submitReservation } = useReservationSubmit(
    {
      restaurantId: businessLocation.restaurantId,
      recaptchaAction: RECAPTCHA_ACTIONS.WIDGET,
      onSuccess: (response, data) => {
        sendMessage({
          type: WIDGET_MESSAGE.BOOKING_COMPLETED,
          payload: {
            reservationId: response.reservationId,
            partySize: data.partySize,
            utcScheduledAt: data.utcScheduledAt,
          },
        });
        onSuccess(response);
      },
    },
  );

  const handleFormSubmit = () => submitReservation(getValues());

  return (
    <>
      <div className="px-4 py-3">
        <ReservationSummary
          time={utcScheduledAt}
          partySize={partySize}
          restaurantNote={businessLocation.integrationConfig.noteToGuest}
          timezone={businessLocation.timezone}
          confirmationEmail={guestEmail}
        />
        {isRecaptchaError && <RecaptchaFailed />}
        {reservationError && <ReservationErrorMessage error={reservationError} />}
        <RecaptchaNoteWithText />
      </div>

      <StickyFooter>
        <Button
          type="button"
          size="large"
          block
          onClick={handleFormSubmit}
          loading={isLoading}
          customDataAttributes={{ root: { testid: 'reservation-summary-submit-button' } }}
        >
          <T
            _str="Book a table"
            _context="Submit button text"
            _tags="reservation"
            _key="Widget.ReservationSummary.Submit.button"
          />
        </Button>
      </StickyFooter>
    </>
  );
}
